import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bot, Copy, KeyRound, Trash2 } from "lucide-react";

const TIER_TEXT = {
  read: "runs on its own", draft: "runs on its own",
  act: "asks you first (MrHelm Away: on its own)", destructive: "always asks you",
};
const auth = () => {
  const t = localStorage.getItem("df_token");
  return { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

/** Settings: let MrHelm's KIT run DataForge (crawls, exports, DieselOps drafts, briefs). */
export default function KitAccess() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [fresh, setFresh] = useState("");
  const load = () => axios.get(`${API}/kit-access`, auth()).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  if (!user || !data) return null;

  const issue = async () => {
    try { const r = await axios.post(`${API}/kit-access`, {}, auth()); setFresh(r.data.token); load(); }
    catch { toast.error("Couldn't issue a KIT key"); }
  };
  const revoke = async () => {
    try { await axios.delete(`${API}/kit-access`, auth()); setFresh(""); load(); toast.success("KIT's access is revoked"); }
    catch { toast.error("Couldn't revoke it"); }
  };
  const base = API.replace(/\/api$/, "");
  const secret = fresh ? JSON.stringify({ url: `${base}/kit`, token: fresh }) : "";
  const tok = data.tokens?.[0];
  const byTier = (t) => (data.tools || []).filter((x) => x.tier === t).map((x) => x.name).join(", ");

  return (
    <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-4" data-testid="kit-access">
      <div className="flex items-center gap-2">
        <Bot className="h-4 w-4 text-sky-400" />
        <h3 className="font-display font-bold text-base">KIT access (MrHelm)</h3>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Lets KIT in MrHelm run DataForge for you: start crawls, export datasets, draft DieselOps knowledge-base rows,
        and write topic briefs and cited facts for Helm. It uses your plan and quotas like you would.
      </p>
      <p className="text-sm text-slate-300">
        {tok ? <>Connected <span className="text-slate-500 font-mono text-xs">({tok.hint}, {tok.last_used ? `last used ${new Date(tok.last_used).toLocaleString()}` : "not used yet"})</span></> : <span className="text-slate-500">Not connected.</span>}
      </p>
      <div className="flex gap-2">
        <Button size="sm" onClick={issue} data-testid="kit-access-issue"><KeyRound className="h-3.5 w-3.5 mr-1.5" />{tok ? "Replace KIT's key" : "Connect KIT"}</Button>
        {tok && <Button size="sm" variant="outline" onClick={revoke}><Trash2 className="h-3.5 w-3.5 mr-1.5" />Revoke</Button>}
      </div>
      {fresh && (
        <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-3 space-y-2">
          <p className="text-xs text-slate-300">Shown once. In Cloud Shell, save it as MrHelm's <code>MCP_SERVER_DATAFORGE</code> secret, then redeploy MrHelm:</p>
          <pre className="text-[11px] font-mono whitespace-pre-wrap break-all select-all text-slate-200">{secret}</pre>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard?.writeText(secret)}><Copy className="h-3.5 w-3.5 mr-1.5" />Copy</Button>
        </div>
      )}
      <div className="text-[11px] text-slate-500 font-mono space-y-1">
        {["read", "draft", "act", "destructive"].map((t) => byTier(t) && (
          <div key={t}><span className="text-slate-300">{t}</span> · {TIER_TEXT[t]}: {byTier(t)}</div>
        ))}
      </div>
    </div>
  );
}
